// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A filter menu component for asset ownership selection.
 * This module provides a searchable interface for filtering
 * assets based on their ownership, with support for both
 * manufacturer and customer selection. It handles complex
 * selection states and search functionality.
 *
 * Key features:
 * - Owner type filtering
 * - Customer selection
 * - Search functionality
 * - Group operations
 * - Real-time updates
 */
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { FilterMenuButton } from '@/components/filter-menu-button';
import { FilterMenuItem } from '@/components/filter-menu-item';
import { SearchRounded } from '@/constants/hardfin-standard-icons';
import { useCustomers } from '@/customer/hooks/use-customers';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useTimeout } from '@/hooks/use-timeout';
import {
  Divider,
  InputAdornment,
  InputBase,
  List,
  Popover,
  Stack,
  useTheme,
} from '@mui/material';
import { without } from 'lodash-es';
import { ChangeEventHandler, useRef, useState } from 'react';

/**
 * Valid ownership types for filtering.
 * Assets can be owned by either the manufacturer
 * or customers.
 *
 * @typedef {string} OwnershipValue
 */
type OwnershipValue = 'manufacturer' | 'customer';

/**
 * Props for configuring the AssetOwnershipFilterMenu component.
 *
 * @interface Props
 */
interface Props {
  /**
   * Currently selected ownership values.
   * Represents high-level ownership categories.
   */
  values: OwnershipValue[];

  /**
   * IDs of specific customers that are selected.
   * Used when individual customers are chosen.
   */
  customerIds: string[];

  /**
   * Callback fired when selection changes.
   * Receives updated arrays for both selection types.
   *
   * @param values - New array of selected ownership types
   * @param customerIds - New array of selected customer IDs
   */
  onChange: (values: OwnershipValue[], customerIds: string[]) => void;
}

/**
 * A searchable filter menu for ownership selection.
 * Provides an interface for selecting asset owners with
 * support for both manufacturer and customer selection,
 * including search functionality and group operations.
 *
 * Features:
 * - Owner type selection
 * - Customer search
 * - Group selection
 * - Real-time filtering
 * - Selection state management
 *
 * Usage example:
 * ```tsx
 * function AssetList() {
 *   const [values, setValues] = useState<OwnershipValue[]>([]);
 *   const [customerIds, setCustomerIds] = useState<string[]>([]);
 *
 *   const handleChange = (
 *     newValues: OwnershipValue[],
 *     newCustomerIds: string[]
 *   ) => {
 *     setValues(newValues);
 *     setCustomerIds(newCustomerIds);
 *   };
 *
 *   return (
 *     <AssetOwnershipFilterMenu
 *       values={values}
 *       customerIds={customerIds}
 *       onChange={handleChange}
 *     />
 *   );
 * }
 * ```
 */
export function AssetOwnershipFilterMenu(props: Props) {
  const theme = useTheme();

  const { user } = useCurrentUser();
  const { customers } = useCustomers();

  const menu = useDisclosure();
  const ref = useRef<HTMLButtonElement>(null);
  const [search, setSearch] = useState('');

  const resetSearch = useTimeout(
    () => setSearch(''),
    theme.transitions.duration.standard
  );

  const canonicalSearchQuery = search.trim().toLowerCase();
  const isOrganizationOptionVisible = user.organization.name
    .toLowerCase()
    .includes(canonicalSearchQuery);

  const options = customers
    .filter(({ name }) => name.toLowerCase().includes(canonicalSearchQuery))
    .map(({ id, name }) => ({
      id,
      label: name,
      isActive:
        props.values.includes('customer') || props.customerIds.includes(id),
    }));

  // The manufacturer (current user's organization) is not included in the
  // options array because it is not a customer. The manufacturer option is
  // considered active when the 'manufacturer' value is included in the values
  // prop, so it should be included in the count.
  const initialCount = props.values.includes('manufacturer') ? 1 : 0;
  const count = options.reduce((count, option) => {
    if (option.isActive) {
      return count + 1;
    }

    return count;
  }, initialCount);

  const isCustomerGroupActive =
    props.values.includes('customer') ||
    customers.every(({ id }) => props.customerIds.includes(id));

  const isCustomerGroupIndeterminate =
    !isCustomerGroupActive &&
    customers.some(({ id }) => props.customerIds.includes(id));

  /**
   * Handles changes to the search input.
   * Updates search state and filters visible options.
   *
   * @param event - The change event from the input
   */
  const handleChangeSearch: ChangeEventHandler<HTMLInputElement> = (event) => {
    setSearch(event.target.value);
  };

  /**
   * Adds an ownership value to the selection.
   * Handles special cases for customer selection.
   *
   * @param value - The ownership value to enable
   */
  const enableOwnershipValue = (value: OwnershipValue) => {
    if (props.values.includes(value)) {
      return;
    }

    if (value === 'customer') {
      return props.onChange([...props.values, value], []);
    }

    props.onChange([...props.values, value], props.customerIds);
  };

  /**
   * Handles selection changes for the manufacturer option.
   * Updates selection state for manufacturer ownership.
   *
   * @param isActive - Whether manufacturer is being selected
   */
  const handleChangeManufacturer = (isActive: boolean) => {
    const values: OwnershipValue[] = isActive
      ? [...props.values, 'manufacturer']
      : without(props.values, 'manufacturer');

    props.onChange(values, props.customerIds);
  };

  /**
   * Handles selection changes for the customer group.
   * Updates selection state for all customers.
   *
   * @param isActive - Whether customers are being selected
   */
  const handleChangeCustomers = (isActive: boolean) => {
    if (isActive) {
      if (canonicalSearchQuery.length === 0) {
        return enableOwnershipValue('customer');
      }

      // The 'option's array is already filtered based on the search query, so
      // this array represents the ID of all customers in the current search
      // result set.
      const customerIds = options.map(({ id }) => id);

      return props.onChange(without(props.values, 'customer'), customerIds);
    }

    props.onChange(without(props.values, 'customer'), []);
  };

  /**
   * Adds a customer to the selection.
   * Handles group completion if all customers selected.
   *
   * @param customerId - ID of customer to add
   */
  const addCustomerId = (customerId: string) => {
    const customerIds = [...props.customerIds, customerId];

    const hasInactiveCustomers = customers.some(
      (customer) => !customerIds.includes(customer.id)
    );

    if (hasInactiveCustomers) {
      return props.onChange(props.values, customerIds);
    }

    enableOwnershipValue('customer');
  };

  /**
   * Removes a customer from the selection.
   * Handles group state updates appropriately.
   *
   * @param customerId - ID of customer to remove
   */
  const removeCustomerId = (customerId: string) => {
    // If the 'customer' value is included in props.values, then all customers
    // are considered active. In this case, removing a single customer ID should
    // remove the 'customer' value from props.values and also add all other
    // customer IDs to the ID array.
    if (props.values.includes('customer')) {
      const customerIds = customers
        .filter((customer) => customer.id !== customerId)
        .map((customer) => customer.id);

      return props.onChange(without(props.values, 'customer'), customerIds);
    }

    const customerIds = without(props.customerIds, customerId);

    props.onChange(props.values, customerIds);
  };

  /**
   * Handles menu close events.
   * Resets search state after closing.
   */
  const handleClose = () => {
    menu.close();
    resetSearch.start();
  };

  return (
    <>
      <FilterMenuButton ref={ref} count={count} onClick={menu.open}>
        Owner
      </FilterMenuButton>
      <Popover
        open={menu.isOpen}
        anchorEl={ref.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        transitionDuration={theme.transitions.duration.standard}
      >
        <Stack paddingTop={1}>
          <InputBase
            size="small"
            placeholder="Search owners"
            onChange={handleChangeSearch}
            sx={{ paddingX: 1, paddingBottom: 0.5 }}
            startAdornment={
              <InputAdornment position="start">
                <SearchRounded />
              </InputAdornment>
            }
          />
          <Divider />
          <List sx={{ overflowY: 'auto', maxHeight: '30rem' }}>
            {isOrganizationOptionVisible && (
              <>
                <FilterMenuItem
                  isActive={props.values.includes('manufacturer')}
                  onChange={handleChangeManufacturer}
                >
                  {user.organization.name}
                </FilterMenuItem>
                <Divider />
              </>
            )}
            {options.length > 0 && (
              <FilterMenuItem
                isActive={isCustomerGroupActive}
                isIndeterminate={isCustomerGroupIndeterminate}
                onChange={handleChangeCustomers}
              >
                Customers
              </FilterMenuItem>
            )}
            {options.map((option) => {
              const handleChange = (isActive: boolean) => {
                if (isActive) {
                  return addCustomerId(option.id);
                }

                removeCustomerId(option.id);
              };

              return (
                <FilterMenuItem
                  key={option.id}
                  level={1}
                  isActive={option.isActive}
                  onChange={handleChange}
                >
                  {option.label}
                </FilterMenuItem>
              );
            })}
          </List>
        </Stack>
      </Popover>
    </>
  );
}
